import NeptuneElement from '../../NeptuneElement.js';

class Select extends NeptuneElement {

    templateMarkup = `
        <div>
            <label></label>
            <div class="select-wrapper">
                <select>
                    <slot></slot>
                </select>
                <span class="dropdown-icon">
                    <i class="fa-solid fa-caret-down"></i>
                </span>
            </div>
            <div>
                <span></span>
                <ul class="neptune-list-custom"></ul>
            </div>
        </div>
    `;

    componentCssUrl = '/src/components/form/select/select.css';

    constructor() {
        super();
    }

    #userErrorText = '';
    #internalErrorTextUpdate = false;
    #onInput = null;
    #onChange = null;
    #onBlur = null;

    async connectedCallback() {
        await super.connectedCallback();
        this.validateOptions();
        this.selectElement = this.shadowRoot.querySelector('select');
        this.#userErrorText = (this.getAttribute('error-text') || '').trim();
        this.handleAccessibilityAttributes();
        this.setLabel(this.getAttribute('label'));
        this.updateMessages();
        this.processInitialAttributesAfterRender(Select.observedAttributes);
        this.populateOptions();
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ['error', 'disabled', 'placeholder', 'label', 'supporting-text', 'error-text', 'required'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!selectElement) {
            return;
        }

        switch (name) {
            case 'disabled': {
                const isDisabled = this.hasAttribute('disabled');
                selectElement.disabled = isDisabled;
                break;
            }
            case 'error': {
                if (this.hasAttribute('error')) {
                    selectElement.setAttribute('aria-invalid', 'true');
                } else {
                    selectElement.removeAttribute('aria-invalid');
                }
                this.updateMessages();
                break;
            }
            case 'placeholder': {
                this.populateOptions();
                break;
            }
            case 'label': {
                this.setLabel(newValue);
                break;
            }
            case 'supporting-text': {
                this.updateMessages();
                break;
            }
            case 'error-text': {
                if (!this.#internalErrorTextUpdate) {
                    this.#userErrorText = (newValue || '').trim();
                }
                this.updateMessages();
                break;
            }
            case 'required': {
                this.hasAttribute('required')
                    ? selectElement.setAttribute('required', '')
                    : selectElement.removeAttribute('required');
                break;
            }
            default: {
                break;
            }
        }
    }

    handleAccessibilityAttributes() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        const labelElement = this.shadowRoot.querySelector('label');
        const supportingTextElement = this.shadowRoot.querySelector('span:not(.dropdown-icon)');
        const errorList = this.shadowRoot.querySelector('ul');

        if (!selectElement || !labelElement || !supportingTextElement || !errorList) {
            return;
        }

        const idBase = Math.random().toString(36).substr(2, 9);
        selectElement.id = `neptune-select-input-${idBase}`;
        labelElement.setAttribute('for', selectElement.id);
        supportingTextElement.id = `neptune-select-support-${idBase}`;
        errorList.id = `neptune-select-errors-${idBase}`;

        selectElement.setAttribute('aria-describedby', `${supportingTextElement.id} ${errorList.id}`);
    }

    setLabel(labelText) {
        const labelElement = this.shadowRoot.querySelector('label');
        if (!labelElement) {
            return;
        }
        labelElement.textContent = (labelText || '').trim();
    }

    updateMessages() {
        const supportingTextElement = this.shadowRoot.querySelector('span:not(.dropdown-icon)');
        const errorList = this.shadowRoot.querySelector('ul');

        if (!supportingTextElement || !errorList) {
            return;
        }

        supportingTextElement.setAttribute('aria-live', 'polite');
        errorList.setAttribute('aria-live', 'polite');

        const supportingText = (this.getAttribute('supporting-text') || '').trim();
        supportingTextElement.textContent = supportingText;
        supportingTextElement.toggleAttribute('hidden', supportingText === '');

        if (this.hasAttribute('error')) {
            const errorText = (this.getAttribute('error-text') || this.#userErrorText).trim();
            const errors = errorText.split('|').filter(e => e.trim());

            errorList.innerHTML = '';
            if (errors.length > 0) {
                errors.forEach(error => {
                    const li = document.createElement('li');
                    li.textContent = error.trim();
                    errorList.appendChild(li);
                });
            }
            errorList.toggleAttribute('hidden', errors.length === 0);

            if (supportingText) {
                supportingTextElement.classList.add('visually-hidden-a11y');
            } else {
                supportingTextElement.classList.remove('visually-hidden-a11y');
            }
            return;
        }

        errorList.innerHTML = '';
        errorList.setAttribute('hidden', '');
        supportingTextElement.classList.remove('visually-hidden-a11y');
    }

    validateInput() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!selectElement) {
            return;
        }

        const label = (this.getAttribute('label') || 'Select').trim();
        const errors = new Set();
        const isRequiredAndEmpty = this.hasAttribute('required') && !selectElement.value;

        if (isRequiredAndEmpty || selectElement.validity.valueMissing) {
            errors.add(this.#userErrorText || `${label} is required`);
        }

        const errorText = [...errors].join('|');

        this.#internalErrorTextUpdate = true;
        try {
            if (errorText) {
                this.setAttribute('error', '');
                this.setAttribute('error-text', errorText);
            } else {
                this.removeAttribute('error');
                this.removeAttribute('error-text');
            }
        } finally {
            this.#internalErrorTextUpdate = false;
        }

        this.updateMessages();
    }

    setEventListeners() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!this.selectElement && selectElement) {
            this.selectElement = selectElement;
        }
        if (!selectElement || this.#onInput || this.#onChange || this.#onBlur) {
            return;
        }

        this.#onInput = () => {
            const val = selectElement.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
        };

        this.#onChange = () => {
            const val = selectElement.value ?? '';
            if (this.getAttribute('value') !== val) {
                this.setAttribute('value', val);
            }
            this.validateInput();
            this.dispatchEvent(new CustomEvent('change', {
                detail: { value: val },
                bubbles: true,
                composed: true
            }));
        };

        this.#onBlur = () => {
            this.validateInput();
        };

        selectElement.addEventListener('input', this.#onInput);
        selectElement.addEventListener('change', this.#onChange);
        selectElement.addEventListener('blur', this.#onBlur);
    }

    removeEventListeners() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!selectElement) {
            return;
        }

        if (this.#onInput) {
            selectElement.removeEventListener('input', this.#onInput);
        }
        if (this.#onChange) {
            selectElement.removeEventListener('change', this.#onChange);
        }
        if (this.#onBlur) {
            selectElement.removeEventListener('blur', this.#onBlur);
        }

        this.#onInput = null;
        this.#onChange = null;
        this.#onBlur = null;
    }

    validateOptions() {
        const options = Array.from(this.querySelectorAll('option'));

        if (options.length === 0) {
            throw new Error('Invalid <neptune-select>: Select component must contain at least one <option> element.');
        }

        const hasValidOption = options.some(option => {
            const value = (option.getAttribute('value') || '');
            const text = (option.textContent || '');
            return value !== '' && text !== '';
        });

        if (!hasValidOption) {
            throw new Error('Invalid <neptune-select>: Select component must contain at least one valid <option> with both value attribute and text content.');
        }
    }

    populateOptions() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!selectElement) {
            return;
        }
        
        selectElement.innerHTML = '';

        const placeholder = this.getAttribute('placeholder');
        if (placeholder) {
            const placeholderOption = document.createElement('option');
            placeholderOption.value = '';
            placeholderOption.textContent = placeholder;
            placeholderOption.disabled = true;
            placeholderOption.selected = true;
            selectElement.appendChild(placeholderOption);
        }

        const options = Array.from(this.querySelectorAll('option'));
        let hasSelectedOption = false;
        let selectedValue = null;

        options.forEach(option => {
            const clonedOption = option.cloneNode(true);
            selectElement.appendChild(clonedOption);

            if (option.selected) {
                hasSelectedOption = true;
                selectedValue = option.value;
            }
        });

        if (hasSelectedOption && selectedValue !== null) {
            selectElement.value = selectedValue;
        }
    }

    get value() {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        return selectElement ? selectElement.value : '';
    }

    set value(val) {
        const selectElement = this.selectElement || this.shadowRoot.querySelector('select');
        if (!selectElement) {
            return;
        }
        selectElement.value = val;
        this.setAttribute('value', val);
    }
}

if (!customElements.get('neptune-select')) {
    customElements.define('neptune-select', Select);
}
export default Select;